/**
 * Instance Pair Store
 *
 * Ephemeral session-level pick of which two streem-backend instances the operator is migrating
 * between. Read by InstancePicker and every migration flow that follows. Cleared on logout.
 *
 * Source state of truth for "what instance are we talking about right now" — pages should NOT
 * call useInstances() and pick randomly; they should subscribe here.
 */

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

type InstancePairState = {
  sourceServiceId: number | null;
  targetServiceId: number | null;

  setSource: (id: number | null) => void;
  setTarget: (id: number | null) => void;
  swap: () => void;
  clear: () => void;
};

export const useInstancePairStore = create<InstancePairState>()(
  persist(
    (set, get) => ({
      sourceServiceId: null,
      targetServiceId: null,

      setSource: (id) => {
        const { targetServiceId } = get();
        // Don't allow source == target. If they collide, clear the target.
        set({
          sourceServiceId: id,
          targetServiceId: id != null && id === targetServiceId ? null : targetServiceId,
        });
      },
      setTarget: (id) => {
        const { sourceServiceId } = get();
        set({
          targetServiceId: id,
          sourceServiceId: id != null && id === sourceServiceId ? null : sourceServiceId,
        });
      },
      swap: () => {
        const { sourceServiceId, targetServiceId } = get();
        set({ sourceServiceId: targetServiceId, targetServiceId: sourceServiceId });
      },
      clear: () => set({ sourceServiceId: null, targetServiceId: null }),
    }),
    {
      name: "mu-instance-pair",
      storage: createJSONStorage(() => sessionStorage),
    }
  )
);
